// The manual-selection state machine: the user-driven system / offset / size / head refs, the
// dropdown lists derived from them, (re)initialising them from the current template, and the
// cascade watchers that keep them consistent as the user changes one. This is the whole
// manual-selection behaviour in one place; stemSelectionMode.ts decides when to enter/clear it.
import { computed, type ComputedRef, nextTick, ref, type Ref, watch } from 'vue';
import { watchImmediate } from '@vueuse/core';
import type { PlannerStore } from '@/views/3d-planner/store/plannerStore.types';
import { getCatalogComponent } from '@/domain/catalog/catalog';
import type { StemOffset, StemSize } from '@/domain/catalog/stem';
import {
    buildSelectableSizes,
    buildSelectableStemOffsets,
    buildSelectableSystemProfiles,
} from './stemSelectableItems';
import { getSelectableHeadOffsetsForSystem } from './stemHeadOffsets';
import { resolveManualSelection } from './resolveManualSelection';
import type {
    SelectableHeadOffset,
    SelectableStemOffset,
    SelectableStemSize,
    SelectableSystemProfile,
    SelectionMode,
} from '../stem.types';
import type { StemConstructs } from './stemConstructs';
import type { StemFiltering } from './stemFiltering';

export type ManualDeps = {
    planner: PlannerStore;
    constructs: StemConstructs;
    filtering: StemFiltering;
    selectionMode: Ref<SelectionMode>;
};

type ManualRefs = {
    selectedSystemProfile: Ref<SelectableSystemProfile | null>;
    selectedOffset: Ref<StemOffset | null>;
    selectedSize: Ref<StemSize | null>;
    selectedHeadOffset: Ref<SelectableHeadOffset | null>;
};

const makeManualRefs = (): ManualRefs => ({
    selectedSystemProfile: ref<SelectableSystemProfile | null>(null),
    selectedOffset: ref<StemOffset | null>(null),
    selectedSize: ref<StemSize | null>(null),
    selectedHeadOffset: ref<SelectableHeadOffset | null>(null),
});

// The dropdown lists derived from the current filtered constructs + the manual refs.
const buildSelectableLists = (deps: ManualDeps, refs: ManualRefs) => {
    const { planner, constructs, filtering } = deps;
    return {
        selectableSystemProfiles: computed<SelectableSystemProfile[]>(() =>
            buildSelectableSystemProfiles(filtering.filteredStemsBySystem.value),
        ),
        selectableStemOffsets: computed<SelectableStemOffset[]>(() =>
            buildSelectableStemOffsets(
                filtering.filteredStemsBySystem.value,
                refs.selectedSystemProfile.value,
            ),
        ),
        selectableSizes: computed<SelectableStemSize[]>(() =>
            buildSelectableSizes(
                constructs.allStemConstructs.value,
                refs.selectedSystemProfile.value,
                refs.selectedOffset.value,
            ),
        ),
        selectableHeadOffsets: computed<SelectableHeadOffset[]>(() => {
            const profile = refs.selectedSystemProfile.value;
            if (!planner.template || !planner.catalog || !profile) {
                return [];
            }
            const headSize = getCatalogComponent(
                planner.catalog.heads,
                planner.template.headUrl,
            ).size;
            return getSelectableHeadOffsetsForSystem({
                heads: planner.catalog.heads,
                stemSystem: profile.system,
                headSize,
                selectedCupSize: planner.selectedCup?.outerDiameter ?? null,
                dualMobility: planner.dualMobility,
            });
        }),
    };
};

// Seed the refs from the current template: mirror the selected stem, or default a switched system.
const seedManualRefs = (deps: ManualDeps, refs: ManualRefs) => {
    const { planner, constructs, filtering } = deps;
    if (!planner.catalog || !planner.template) {
        return;
    }
    const result = resolveManualSelection({
        catalogStems: planner.catalog.stems,
        catalogHeads: planner.catalog.heads,
        templateStemUrl: planner.template.stemUrl,
        templateHeadUrl: planner.template.headUrl,
        preferredSystem: constructs.preferredSystem.value,
        selectedStemSystem: filtering.selectedStemSystem.value,
        selectedCupSize: planner.selectedCup?.outerDiameter ?? null,
        dualMobility: planner.dualMobility,
    });
    if (result.mode === 'matched') {
        refs.selectedSystemProfile.value = result.systemProfile;
        refs.selectedOffset.value = result.offset;
        refs.selectedSize.value = result.size;
        refs.selectedHeadOffset.value = result.headOffset;
        constructs.selectedHead.value = result.headOffset;
        return;
    }
    refs.selectedSystemProfile.value = null;
    refs.selectedOffset.value = null;
    refs.selectedSize.value = null;
    if (result.headOffset) {
        refs.selectedHeadOffset.value = result.headOffset;
    }
};

type CascadeContext = {
    refs: ManualRefs;
    selectableSizes: ComputedRef<SelectableStemSize[]>;
    constructs: StemConstructs;
    selectionMode: Ref<SelectionMode>;
    isInitialising: Ref<boolean>;
};

// Keep the dependent refs consistent as the user changes one. Skipped during (re)initialisation,
// which sets all four refs at once under the `isInitialising` guard.
const registerManualCascade = (ctx: CascadeContext) => {
    const { refs, selectableSizes, constructs, selectionMode, isInitialising } = ctx;

    // Size chosen → load that construct, applying the chosen head with it.
    watch(refs.selectedSize, (size) => {
        const selected = selectableSizes.value.find((s) => s.size === size);
        if (selected && refs.selectedSystemProfile.value) {
            constructs.selectedStem.value = selected.stemUrl;
            constructs.selectedHead.value = refs.selectedHeadOffset.value;
        }
    });

    // System profile changed → the downstream offset and size no longer apply.
    watchImmediate(refs.selectedSystemProfile, (newProfile, oldProfile) => {
        if (isInitialising.value) return;
        if (oldProfile !== undefined && newProfile !== oldProfile) {
            refs.selectedOffset.value = null;
            refs.selectedSize.value = null;
        }
    });

    // Offset changed → keep the current size if it still exists for the new offset, else reset it.
    watchImmediate(refs.selectedOffset, (newOffset, oldOffset) => {
        if (isInitialising.value) return;
        if (oldOffset !== undefined && newOffset !== oldOffset) {
            if (refs.selectedSize.value !== null && newOffset !== null) {
                const match = selectableSizes.value.find((s) => s.size === refs.selectedSize.value);
                if (match) {
                    constructs.selectedStem.value = match.stemUrl;
                    return;
                }
            }
            refs.selectedSize.value = null;
        }
    });

    // Head offset chosen → apply it once a full manual construct (profile + offset + size) is selected.
    watchImmediate(refs.selectedHeadOffset, (newHead) => {
        if (
            selectionMode.value === 'manual' &&
            refs.selectedSystemProfile.value &&
            refs.selectedOffset.value &&
            refs.selectedSize.value !== null &&
            newHead &&
            constructs.selectedHead.value?.headUrl !== newHead.headUrl
        ) {
            constructs.selectedHead.value = newHead;
        }
    });
};

export const useManualSelection = (deps: ManualDeps) => {
    const refs = makeManualRefs();
    const lists = buildSelectableLists(deps, refs);
    // Suppresses the cascade watchers while (re)initialisation sets all four refs at once.
    const isInitialising = ref(false);

    // Seed the refs under the guard so the cascade watchers don't fire mid-(re)initialisation.
    const reinitialise = async () => {
        isInitialising.value = true;
        seedManualRefs(deps, refs);
        await nextTick();
        isInitialising.value = false;
    };

    const clear = () => {
        refs.selectedSystemProfile.value = null;
        refs.selectedOffset.value = null;
        refs.selectedSize.value = null;
        refs.selectedHeadOffset.value = null;
    };

    registerManualCascade({
        refs,
        selectableSizes: lists.selectableSizes,
        constructs: deps.constructs,
        selectionMode: deps.selectionMode,
        isInitialising,
    });

    return { ...refs, ...lists, reinitialise, clear };
};

export type StemManualSelection = ReturnType<typeof useManualSelection>;
